// src/components/explorers/wildfireExplorer/tabs/ModelsTab.tsx
import React from 'react';
import { WildfireEntity } from '../../../../types/wildfire';
import {
  useFirestarrRuns,
  FIRESTARR_TIMEFRAMES,
} from '../../../../hooks/useFirestarrRuns';
import './ModelsTab.scss';

interface ModelsTabProps {
  wildfire: WildfireEntity;
}

/**
 * Status as a duty officer reads it — #929 M5.
 *
 * Words, not colour. `complete` and `failed` must be separable by luminance
 * rather than hue (red-green colourblindness is a real constraint here), and a
 * text label is separable without any styling at all.
 *
 * `pending` says "not started" rather than "pending" alone, because the
 * criterion this panel exists to meet is that a failed run is distinguishable
 * from one NOT YET STARTED — and on the map both render as a blank timeframe.
 */
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending — not started',
  processing: 'Processing — in flight',
  complete: 'Complete',
  failed: 'Failed',
};

export const ModelsTab: React.FC<ModelsTabProps> = ({ wildfire }) => {
  const data = wildfire.data || wildfire;
  const { runs, loading, error } = useFirestarrRuns(wildfire);

  return (
    <div className="wildfire-detail-content">
      {/* Fire Information */}
      <section className="detail-section">
        <h3>🔥 Fire Behavior Modelling</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Fire Number:</label>
            <span>{data.number || wildfire.fire_number}</span>
          </div>
          <div className="detail-item">
            <label>Status:</label>
            <span>{data.wildfireStatus || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Size:</label>
            <span>{data.estimatedSize ? `${data.estimatedSize} Ha` : 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Cause:</label>
            <span>{data.cause || 'N/A'}</span>
          </div>
        </div>
      </section>

      {/* FireSTARR run state — #929 M5 */}
      <section className="detail-section firestarr-runs-section">
        <h3>FireSTARR Runs</h3>
        {/*
          * The horizon list is drawn ONLY when the runs are actually known.
          * Drawing it while loading, or after a failed request, would render
          * seven "Not scheduled" rows from an empty list — stating as fact
          * that the harness skipped this fire when the truth is that nobody
          * knows. A confident wrong answer is worse than the blank it
          * replaced.
          */}
        {loading && (
          <p className="firestarr-run-list__loading" data-testid="firestarr-runs-loading">
            Loading run state…
          </p>
        )}
        {!loading && error !== null && (
          <p className="firestarr-run-list__error" data-testid="firestarr-runs-error">
            Run state could not be loaded: {error}
          </p>
        )}
        {!loading && error === null && (
        <ul className="firestarr-run-list">
          {FIRESTARR_TIMEFRAMES.map((days) => {
            const run = runs.find((r) => r.timeframe_days === days);
            return (
              <li
                key={days}
                className={`firestarr-run firestarr-run--${run?.status ?? 'unscheduled'}`}
                data-testid={`firestarr-run-${days}`}
              >
                <span className="firestarr-run__timeframe">Day {days}</span>
                <span className="firestarr-run__status">
                  {run === undefined
                    ? 'Not scheduled'
                    : STATUS_LABEL[run.status] ?? run.status}
                </span>
                {run?.error != null && (
                  <span className="firestarr-run__error">{run.error}</span>
                )}
              </li>
            );
          })}
        </ul>
        )}
      </section>
    </div>
  );
};
